import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { MatchingLanguage } from '@adornis/bh-matching-user/entities/auto-matching-fields.js';
import { Gender, GenderPreference } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { MatchingRole } from '@adornis/bh-matching-user/roles.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { ensureTestUser } from '@adornis/users/server/ensureTestUser.js';
import { format } from 'date-fns';
import {
  GradeLevel,
  MatchingDayDate,
  MatchingHautnahMenteeUser,
  MatchingHautnahMentorUser,
  PreferredLanguage,
  StudyProgram,
} from '../entities/matching-user.js';

/**
 * Inserts test data into the database for testing the matching-hautnah application
 * Creates sample mentors and mentees with realistic data
 */
export const insertTestData = registerMutation({
  operationName: 'insertTestData',
  type: () => Boolean,
  params: [],
  resolve: () => async () => {
    if (baseConfig.get('DEPLOYMENT') !== 'development') throw 'dev only';

    logger.info('Starting to insert test data for matching-hautnah');

    // First, empty the database of existing test data
    logger.info('Emptying database of existing test data');
    await mongo().collection(MatchingHautnahMentorUser._collectionName).deleteMany({});
    await mongo().collection(MatchingHautnahMenteeUser._collectionName).deleteMany({});
    await mongo().collection(Timeframe._collectionName).deleteMany({});
    await ensureTestUser(AdornisUser, {
      roles: [new AdornisRoleToContextsWrapper({ name: MatchingRole.ADMIN, contexts: [GLOBAL_CONTEXT] })],
    });

    logger.info('Database emptied successfully');

    await runInContext(serverContext.sessionID, async () => {
      // Create a timeframe for the test data
      const currentDate = new Date();
      const nextSemesterStart = new Date(currentDate);
      nextSemesterStart.setMonth(currentDate.getMonth() + 1);

      const nextSemesterEnd = new Date(nextSemesterStart);
      nextSemesterEnd.setMonth(nextSemesterStart.getMonth() + 6);

      const semesterName = `Test Semester ${format(nextSemesterStart, 'yyyy-MM')}`;

      // Create a new timeframe for our test data
      const timeframe = new Timeframe({
        name: semesterName,
        from: nextSemesterStart,
        to: nextSemesterEnd,
        isActive: true,
        institutionID: 'global',
        publishDate: new Date(), // Adding required publishDate field
      });

      await timeframe.create();
      logger.info({ id: timeframe._id, name: timeframe.name }, 'Created test timeframe');

      // The legacy fixtures only specified birth years; use January 1 for their required birth dates.
      // Keep details without dedicated fields in additionalNotes.
      // Create test mentors
      const mentors = [
        new MatchingHautnahMentorUser({
          firstName: 'Thomas',
          lastName: 'Müller',
          dateOfBirth: new Date(1990, 0, 1),
          studyPrograms: [StudyProgram.STUDY_COMPUTER_SCIENCE],
          gender: Gender.MALE,
          genderPreference: GenderPreference.NO_PREFERENCE,
          nativeLanguage: MatchingLanguage.Deutsch,
          foreignLanguages: [MatchingLanguage.Englisch, MatchingLanguage.Chinesisch], // Li Wang speaks Chinese (recorded in the mentee notes).
          wunschsprache: PreferredLanguage.GERMAN_ENGLISH, // Li Wang shares this preference (recorded in the mentee notes).
          numberOfMentees: 2,
          email: 'thomas.mueller@example.com',
          username: 'thomas.mueller',
          timeframeID: timeframe._id,
          note: 'Experienced in mentoring computer science students.',
          availableDates: Object.values(MatchingDayDate),
          dataPrivacyConsent: true,
          additionalNotes:
            'Wohnort: Berlin; Land: Deutschland; Herkunftsland: Deutschland; Staatsangehörigkeit: Deutsch; Fachbereich: Informatik; Abschluss: Master; Hobbies: Fußball, Musik, Programmieren',
        }),
        new MatchingHautnahMentorUser({
          firstName: 'Sarah',
          lastName: 'Schmidt',
          dateOfBirth: new Date(1988, 0, 1),
          studyPrograms: [StudyProgram.STUDY_ECONOMICS],
          gender: Gender.FEMALE,
          genderPreference: GenderPreference.SAME_SEX,
          nativeLanguage: MatchingLanguage.Deutsch,
          foreignLanguages: [MatchingLanguage.Englisch, MatchingLanguage.Franzoesisch],
          wunschsprache: PreferredLanguage.GERMAN,
          numberOfMentees: 1,
          email: 'sarah.schmidt@example.com',
          username: 'sarah.schmidt',
          timeframeID: timeframe._id,
          availableDates: Object.values(MatchingDayDate),
          dataPrivacyConsent: true,
          additionalNotes:
            'Wohnort: Hamburg; Land: Deutschland; Herkunftsland: Deutschland; Staatsangehörigkeit: Deutsch; Fachbereich: Wirtschaftswissenschaften; Abschluss: Master; Hobbies: Lesen, Reisen, Yoga',
        }),
        new MatchingHautnahMentorUser({
          firstName: 'Michael',
          lastName: 'Becker',
          dateOfBirth: new Date(1992, 0, 1),
          studyPrograms: [StudyProgram.STUDY_PHYSICS],
          gender: Gender.MALE,
          genderPreference: GenderPreference.NO_PREFERENCE,
          nativeLanguage: MatchingLanguage.Deutsch,
          foreignLanguages: [MatchingLanguage.Englisch],
          wunschsprache: PreferredLanguage.ENGLISH,
          numberOfMentees: 3,
          email: 'michael.becker@example.com',
          username: 'michael.becker',
          timeframeID: timeframe._id,
          availableDates: Object.values(MatchingDayDate),
          dataPrivacyConsent: true,
          additionalNotes:
            'Wohnort: München; Land: Deutschland; Herkunftsland: Österreich; Staatsangehörigkeit: Österreichisch; Fachbereich: Naturwissenschaften; Abschluss: Promotion; Hobbies: Wandern, Fotografie, Kochen',
        }),
      ];

      // The original adult mentees are graduates; use the smaller supported group size.
      // Create test mentees
      const mentees = [
        new MatchingHautnahMenteeUser({
          firstName: 'Li',
          lastName: 'Wang',
          dateOfBirth: new Date(1998, 0, 1),
          primaryStudyInterest: StudyProgram.STUDY_COMPUTER_SCIENCE,
          gender: Gender.FEMALE,
          genderPreference: GenderPreference.NO_PREFERENCE, // Compatible with Thomas Müller
          email: 'li.wang@example.com',
          username: 'li.wang',
          timeframeID: timeframe._id,
          note: 'Looking for help with German language and understanding university system.',
          availableDates: Object.values(MatchingDayDate),
          dataPrivacyConsent: true,
          gradeLevel: GradeLevel.GRADUATED,
          singleRegistrationConfirmed: true,
          numberOfFellowMentees: 2,
          additionalNotes:
            'Wohnort: Berlin; Land: Deutschland; Herkunftsland: China; Staatsangehörigkeit: Chinesisch; Fachbereich: Informatik; Abschluss: Bachelor; Hobbies: Schach, Klavier, Programmieren; Muttersprache: Chinesisch; Fremdsprachen: Englisch, Deutsch; Wunschsprache: GERMAN_ENGLISH',
        }),
        new MatchingHautnahMenteeUser({
          firstName: 'Carlos',
          lastName: 'Rodriguez',
          dateOfBirth: new Date(1999, 0, 1),
          primaryStudyInterest: StudyProgram.STUDY_ECONOMICS,
          gender: Gender.MALE,
          genderPreference: GenderPreference.SAME_SEX,
          email: 'carlos.rodriguez@example.com',
          username: 'carlos.rodriguez',
          timeframeID: timeframe._id,
          availableDates: Object.values(MatchingDayDate),
          dataPrivacyConsent: true,
          gradeLevel: GradeLevel.GRADUATED,
          singleRegistrationConfirmed: true,
          numberOfFellowMentees: 2,
          additionalNotes:
            'Wohnort: München; Land: Deutschland; Herkunftsland: Spanien; Staatsangehörigkeit: Spanisch; Fachbereich: Wirtschaftswissenschaften; Abschluss: Bachelor; Hobbies: Fußball, Gitarre, Kochen; Muttersprache: Spanisch; Fremdsprachen: Englisch; Wunschsprache: GERMAN',
        }),
        new MatchingHautnahMenteeUser({
          firstName: 'Fatima',
          lastName: 'Hassan',
          dateOfBirth: new Date(1997, 0, 1),
          primaryStudyInterest: StudyProgram.STUDY_COMPARATIVE_LITERATURE,
          gender: Gender.FEMALE,
          genderPreference: GenderPreference.SAME_SEX,
          email: 'fatima.hassan@example.com',
          username: 'fatima.hassan',
          timeframeID: timeframe._id,
          availableDates: Object.values(MatchingDayDate),
          dataPrivacyConsent: true,
          gradeLevel: GradeLevel.GRADUATED,
          singleRegistrationConfirmed: true,
          numberOfFellowMentees: 2,
          additionalNotes:
            'Wohnort: Hamburg; Land: Deutschland; Herkunftsland: Marokko; Staatsangehörigkeit: Marokkanisch; Fachbereich: Geisteswissenschaften; Abschluss: Master; Hobbies: Lesen, Schreiben, Kunst; Muttersprache: Arabisch; Fremdsprachen: Franzoesisch, Englisch, Deutsch; Wunschsprache: GERMAN_ENGLISH',
        }),
        new MatchingHautnahMenteeUser({
          firstName: 'David',
          lastName: 'Smith',
          dateOfBirth: new Date(2000, 0, 1),
          primaryStudyInterest: StudyProgram.STUDY_CHEMISTRY,
          gender: Gender.MALE,
          genderPreference: GenderPreference.NO_PREFERENCE,
          email: 'david.smith@example.com',
          username: 'david.smith',
          timeframeID: timeframe._id,
          availableDates: Object.values(MatchingDayDate),
          dataPrivacyConsent: true,
          gradeLevel: GradeLevel.GRADUATED,
          singleRegistrationConfirmed: true,
          numberOfFellowMentees: 2,
          additionalNotes:
            'Wohnort: Berlin; Land: Deutschland; Herkunftsland: USA; Staatsangehörigkeit: Amerikanisch; Fachbereich: Naturwissenschaften; Abschluss: Bachelor; Hobbies: Basketball, Gaming, Reisen; Muttersprache: Englisch; Fremdsprachen: Deutsch; Wunschsprache: ENGLISH',
        }),
      ];

      // Save all test data to the database
      for (const mentor of mentors) {
        await mentor.create();
        logger.info({ id: mentor._id, email: mentor.email }, 'Created test mentor');
      }

      for (const mentee of mentees) {
        await mentee.create();
        logger.info({ id: mentee._id, email: mentee.email }, 'Created test mentee');
      }

      // Manually create matches between compatible mentors and mentees
      // This ensures we have at least one match in our test data
      // Thomas Müller and Li Wang share available dates, a study program, and the preferred group size.
      try {
        const thomasMueller = mentors.find(m => m.username === 'thomas.mueller');
        const liWang = mentees.find(m => m.username === 'li.wang');

        if (thomasMueller && liWang) {
          // Create a match between Thomas and Li
          const match = new Match({
            mentee: liWang,
            menteeID: liWang._id,
            mentor: thomasMueller,
            mentorID: thomasMueller._id,
            timeframeID: timeframe._id,
          });

          await match.create();

          logger.info(
            {
              matchId: match._id,
              mentorName: thomasMueller.firstName + ' ' + thomasMueller.lastName,
              menteeName: liWang.firstName + ' ' + liWang.lastName,
            },
            'Created match between compatible mentor and mentee',
          );
        }
      } catch (error) {
        logger.error({ error }, 'Failed to create matches');
      }

      logger.info('Test data insertion completed successfully');
    });

    return true;
  },
});
